import { getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { fetchCurrentUser } from "@/lib/api/users";
import { levelAtLeast, normalizeHiddenClinics, normalizeHiddenMenuItems, normalizePermissionsMap } from "@/lib/permissions";

// Module-level RBAC state for the logged-in user, mirroring the event-emitter shape of
// user-preference-store. Seeded synchronously from the cached auth user (login payload)
// and re-seeded on every auth-state change; refreshPermissions() replaces the seed with a
// fresh GET /api/users/me. hasPermission() is the single gate the nav/pages consult.
const PERMISSIONS_CHANGE_EVENT = "healo-permissions-change";

const LOGGED_OUT_STATE = Object.freeze({
  isAdmin: false,
  isEmployee: false,
  internalChatEligible: false,
  permissions: Object.freeze({}),
  // Nav items hidden by the user's groups (union, admins always empty) — UI trimming only.
  hiddenMenuItems: Object.freeze([]),
  // Messages clinic tabs (WhatsApp botIds) hidden by the user's groups (union, admins always
  // empty) — UI trimming only, same as hiddenMenuItems.
  hiddenClinics: Object.freeze([]),
  // "none"  → logged out; "seed" → from the cached login payload; "server" → from /users/me.
  source: "none",
  // hasServerMap distinguishes "the backend sent a permissions map (possibly empty)" from
  // "this session predates RBAC" — the legacy fallback below only applies to the latter.
  hasServerMap: false,
});

let state = LOGGED_OUT_STATE;
let seededFromAuth = false;

function buildStateFromUser(user, source) {
  if (!user || typeof user !== "object") {
    return LOGGED_OUT_STATE;
  }
  const rawPermissions =
    user.permissions && typeof user.permissions === "object" ? user.permissions : null;
  return Object.freeze({
    isAdmin: Boolean(user.isAdmin),
    isEmployee: Boolean(user.isEmployee),
    internalChatEligible: Boolean(user.internalChatEligible),
    permissions: Object.freeze(normalizePermissionsMap(rawPermissions)),
    hiddenMenuItems: Object.freeze(user.isAdmin ? [] : normalizeHiddenMenuItems(user.hiddenMenuItems)),
    hiddenClinics: Object.freeze(user.isAdmin ? [] : normalizeHiddenClinics(user.hiddenClinics)),
    source,
    hasServerMap: rawPermissions !== null,
  });
}

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(PERMISSIONS_CHANGE_EVENT));
}

function statesEqual(a, b) {
  if (a === b) return true;
  if (
    a.isAdmin !== b.isAdmin ||
    a.isEmployee !== b.isEmployee ||
    a.internalChatEligible !== b.internalChatEligible ||
    a.source !== b.source ||
    a.hasServerMap !== b.hasServerMap
  ) {
    return false;
  }
  if (
    a.hiddenMenuItems.length !== b.hiddenMenuItems.length ||
    !a.hiddenMenuItems.every((k, i) => k === b.hiddenMenuItems[i])
  ) {
    return false;
  }
  if (
    a.hiddenClinics.length !== b.hiddenClinics.length ||
    !a.hiddenClinics.every((k, i) => k === b.hiddenClinics[i])
  ) {
    return false;
  }
  const aKeys = Object.keys(a.permissions);
  const bKeys = Object.keys(b.permissions);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => a.permissions[k] === b.permissions[k]);
}

function setState(next) {
  if (statesEqual(state, next)) return;
  state = next;
  emitChange();
}

function seedFromAuthUser() {
  setState(buildStateFromUser(getAuthUser(), "seed"));
}

function ensureSeeded() {
  if (seededFromAuth) return;
  seededFromAuth = true;
  seedFromAuthUser();
  // Re-seed on every login/logout; the auth event also fires for token-only changes,
  // which the statesEqual guard turns into no-ops.
  subscribeAuthStateChange(() => {
    seedFromAuthUser();
  });
}

export function getPermissionsState() {
  ensureSeeded();
  return state;
}

export function subscribePermissions(listener) {
  ensureSeeded();
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getPermissionsState());
  window.addEventListener(PERMISSIONS_CHANGE_EVENT, handler);
  return () => {
    window.removeEventListener(PERMISSIONS_CHANGE_EVENT, handler);
  };
}

// refreshPermissions replaces the login-payload seed with a fresh /users/me read. It must
// NOT write back via setAuthUser (that emits the auth event and would loop the seeding).
// Errors keep the current state — a transient fetch failure must not blank the UI.
// Identity guard: the response is stamped with the userId active when the fetch STARTED and
// dropped on mismatch — a profile switch mid-flight must not land user A's RBAC in user B's
// session (the page remount cannot cancel promises writing into this module singleton).
let refreshInFlight = null;
let refreshInFlightUserId = "";
export function refreshPermissions() {
  ensureSeeded();
  const requestUserId = String(getAuthUser()?.id || "").trim();
  if (refreshInFlight && refreshInFlightUserId === requestUserId) return refreshInFlight;
  refreshInFlightUserId = requestUserId;
  const request = fetchCurrentUser()
    .then((res) => {
      const user = res?.user;
      if (user && String(getAuthUser()?.id || "").trim() === requestUserId) {
        setState(buildStateFromUser(user, "server"));
      }
      return getPermissionsState();
    })
    .catch(() => getPermissionsState())
    .finally(() => {
      if (refreshInFlight === request) {
        refreshInFlight = null;
        refreshInFlightUserId = "";
      }
    });
  refreshInFlight = request;
  return request;
}

// hasPermission(module, level) — THE access gate. Admins pass everything. While the session
// predates the RBAC backend (no permissions map on the user), fall back to the legacy
// behavior so nothing breaks mid-rollout: every module acts as manage EXCEPT the ones whose
// surface was NOT open to every employee before RBAC — chats (the old internalChatEligible
// boolean), staff (admins only) and integrations (WhatsApp Connections was adminOnly). Once a
// server map exists the check is strict: a missing module means none (fail closed).
export function hasPermission(module, level = "view") {
  const s = getPermissionsState();
  if (s.source === "none") return false;
  if (s.isAdmin) return true;
  if (!s.hasServerMap) {
    if (module === "chats") return s.internalChatEligible;
    // Fail closed for pre-RBAC surfaces that were never open to ordinary employees — granting
    // them here would be an escalation, not a compatibility shim. (Admins already returned
    // true above.) ai_drafts and work_plan are deliberately NOT in this list: every employee
    // could draft replies and open the planner before the modules were carved out.
    if (module === "staff" || module === "integrations") return false;
    return true;
  }
  if (module === "chats" && !s.internalChatEligible) {
    // The server composes chats eligibility (userType config AND the chats module);
    // internalChatEligible is that composed answer, so honour it directly.
    return false;
  }
  return levelAtLeast(s.permissions[module] ?? "none", level);
}

export function isAdminUser() {
  return getPermissionsState().isAdmin;
}

// isMenuItemHidden — nav-sidebar trimming from the user's groups. Purely cosmetic (the
// module gate in hasPermission stays the access decision); admins never hide anything.
export function isMenuItemHidden(menuKey) {
  const s = getPermissionsState();
  if (s.isAdmin) return false;
  return s.hiddenMenuItems.includes(menuKey);
}

// isClinicHidden — Messages clinic-tab trimming from the user's groups (WhatsApp botId).
// Purely cosmetic: the "All" tab still includes the hidden clinic's threads; admins see every
// tab. Mirrors isMenuItemHidden.
export function isClinicHidden(botId) {
  const key = typeof botId === "string" ? botId.trim() : "";
  if (!key) return false;
  const s = getPermissionsState();
  if (s.isAdmin) return false;
  return s.hiddenClinics.includes(key);
}
